import type * as Types from '@aeppic/types'
import { Form } from '../model/form.js'
import { ControlLookup, ControlMatchStrategy } from './lookup/control-lookup.js'
import { classicFindControl } from './components/ae-control-selector.js'
import { controlNameForPlaceholder, normalizeControlLookupVariant, type ControlLookupVariant } from './forms/preload-controls.js'
import type { ParsedPlaceholder } from '@aeppic/forms-parser'
import { useServerLookup } from './lookup/client-lookup.js'

const CONTROL_FIND_OPTIONS = {
  limit: 100,
  sort: 'created.at',
  fields: [] as string[],
  tag: 'form-controls',
}

/**
 * The control lookup used by ae-control. Form preload calls this so a form
 * resolves placeholders with the same rules as a single control lookup.
 */
export class Controls {
  constructor(private _aeppic) {}

  async resolveForForm(form: Form, variant: ControlLookupVariant = {}): Promise<Map<number, Types.Document | null>> {
    const options = this._aeppic.Features?.getOptionsFor?.('control-lookup') ?? {}
    const featureOn = this._aeppic.Features?.isEnabled?.('control-lookup') !== false
    const strategyName = options.strategy ?? 'full'
    if (!featureOn || strategyName === 'classic') {
      return this._classicForPlaceholders(form, variant)
    }
    const strategy = strategyName === 'system'
      ? ControlMatchStrategy.SYSTEM
      : strategyName === 'default-only'
        ? ControlMatchStrategy.DEFAULT_ONLY
        : ControlMatchStrategy.FULL
    const lookup = new ControlLookup(this._aeppic, strategy)
    let resolved: Map<number, Types.Document | null>
    if (useServerLookup(this._aeppic, 'controls')) {
      const normalizedVariant = normalizeControlLookupVariant(variant)
      const result = await this._aeppic.lookupLocally?.({
        kind: 'controls', formId: form.id, version: form.v, strategy: ControlMatchStrategy[strategy], variant: normalizedVariant,
      }) ?? await this._fetchControls(form, ControlMatchStrategy[strategy], normalizedVariant)
      resolved = new Map()
      const references = new Map<string, Types.IdOrReference>()
      for (const [offset, id] of Object.entries(result.controls)) {
        const reference = result.references?.[offset] ?? id
        if (reference) references.set(JSON.stringify(reference), reference)
      }
      // Submit unique references together so the normal document loader can batch missing reads.
      const keys = [...references.keys()]
      const loaded = keys.length ? await this._aeppic.getAll([...references.values()]) : []
      const documents = new Map(keys.map((key, index) => [key, loaded[index]]))
      for (const [offset, id] of Object.entries(result.controls)) {
        const reference = result.references?.[offset] ?? id
        resolved.set(Number(offset), reference ? documents.get(JSON.stringify(reference)) ?? null : null)
      }
    } else {
      resolved = await lookup.findControlsForPlaceholders(form, query => this._aeppic.find(query, CONTROL_FIND_OPTIONS), variant)
    }
    if (options.fallback !== true) return resolved
    await this._fillMissesFromClassic(form, resolved, variant)
    return resolved
  }

  private async _fetchControls(form: Form, strategy: string, variant: ControlLookupVariant) {
    const params = new URLSearchParams({ v: form.v, strategy, ...variant })
    const response = await this._aeppic.fetch(`/api/forms/${encodeURIComponent(form.id)}/controls?${params}`)
    if (!response.ok) throw new Error(`Control preload failed: ${response.status}`)
    return response.json()
  }

  private async _classicForPlaceholders(form: Form, variant: ControlLookupVariant): Promise<Map<number, Types.Document | null>> {
    const resolved = new Map<number, Types.Document | null>()
    const placeholders = form.getPlaceholders().filter(placeholder => placeholder.offset != null && (placeholder.field || placeholder.control))
    await Promise.all(placeholders.map(async placeholder => {
      resolved.set(placeholder.offset, await this._classic(form, placeholder, variant))
    }))
    return resolved
  }

  private async _fillMissesFromClassic(form: Form, resolved: Map<number, Types.Document | null>, variant: ControlLookupVariant) {
    const missing = form.getPlaceholders().filter(placeholder => {
      if (placeholder.offset == null || (!placeholder.field && !placeholder.control)) return false
      return !resolved.get(placeholder.offset)
    })
    await Promise.all(missing.map(async placeholder => {
      resolved.set(placeholder.offset, await this._classic(form, placeholder, variant))
    }))
  }

  private async _classic(form: Form, placeholder: ParsedPlaceholder, variant: ControlLookupVariant): Promise<Types.Document | null> {
    const control = await classicFindControl(
      this._aeppic,
      { id: form.id, v: form.v },
      placeholder.field?.name,
      controlNameForPlaceholder(placeholder),
      Object.keys(normalizeControlLookupVariant(variant)).length > 0,
      undefined,
      variant,
    )
    return control ?? null
  }
}
